import React, { useState, useMemo, useEffect } from "react";
import { VStack, HStack, Text, Box, Pressable, Center } from "native-base";
import Moment from "moment";
import * as AV from "../../../AVCore/AVCore";
import ProfilePic from "../../common/ProfilePicture";
import { Loader } from "../../../components/loaders";
import { Loc } from "../../../App/Loc";
import { RootStateOrAny, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PurchaseCreditsPopup from "../../../components/common/PurchaseCreditsPopup";
import { DollarCoinsIcon } from "../../../assets/icons/icon-assets";

interface Props {
  entity?: AV.IAVEntity;
  sharedPlan?: AV.ISharedPlan;
  setSharedPlan?: (sharedPlan: AV.ISharedPlan) => void;
  planType?: "MyPayments" | "MyTransactions" | "MyCollections";
  isAutoTrans?: boolean;
  triggerBuyCredits?: boolean;
  setTriggerBuyCredits?: (val: boolean) => void;
}

function MyFunds({
  entity,
  sharedPlan,
  setSharedPlan,
  planType,
  isAutoTrans,
  triggerBuyCredits,
  setTriggerBuyCredits,
}: Props) {
  const navigate = useNavigate();
  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const [myPlans, setMyPlans] = useState<
    AV.ICollectLog[] | AV.ICreditTransaction[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [creditsAvailable, setCreditsAvailable] = useState<number>(
    entity ? (entity?.plan?.credits ?? 0) : (currentUser?.plan?.credits ?? 0),
  );
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  const [Entity, setEntity] = useState(entity ? entity : currentUser);

  const formatDate = (date: string | Date) => {
    return Moment(new Date(date)).format("MMMM D YYYY, h:mm A");
  };

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const finalEntity = entity || currentUser;
        if (!finalEntity) return;

        const Fk = AV.AVEntity.getFk(finalEntity);
        if (!Fk?.id) return;

        if (planType === "MyPayments") {
          const payments: AV.ICollectLog[] = await AV.Plan.payments(Fk);
          if (isMounted) setMyPlans(payments);
        } else if (planType === "MyTransactions") {
          const transactions: AV.ICreditTransaction[] =
            await AV.Plan.getTransactions(Fk);
          if (isMounted) setMyPlans(transactions);
        }
      } catch (error) {
        console.error("Failed to load payments/transactions:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPlans();
    setCreditsAvailable(
      Entity ? (Entity?.plan?.credits ?? 0) : (currentUser?.plan?.credits ?? 0),
    );

    return () => {
      isMounted = false;
    };
  }, [entity, currentUser, planType]);

  //useEffect(() => {
  //  if (sharedPlan) {
  //    setCreditsAvailable(sharedPlan?.plan?.credits ?? 0);
  //  }
  //}, [sharedPlan]);

  const filteredSortedPayments = useMemo(() => {
    return (
      myPlans
        //?.filter((p) => p.type === "P")
        .sort((a, b) => {
          try {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          } catch (err) {
            return 0;
          }
        }) || []
    );
  }, [myPlans]);

  return (
    <>
      <Box
        bg="white"
        p={6}
        borderRadius="lg"
        shadow="sm"
        width="100%"
        maxWidth="800px"
        height="calc(100vh - 150px)"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        {loading ? (
          <Box position="absolute" top="40%" left="40%" m={5}>
            <Loader />
          </Box>
        ) : (
          <>
            <HStack justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontSize="md" fontWeight="medium">
                {planType === "MyPayments"
                  ? Loc.currentLang.menu.myPayments
                  : planType === "MyCollections"
                    ? Loc.currentLang.menu.myCollections
                    : Loc.currentLang.menu.myTransactions}
              </Text>
              <HStack alignItems="center" space={2}>
                <Text fontSize="sm" color="gray.600">
                  {Loc.currentLang.purchaseCredits.Credits}
                </Text>
                <Box
                  bg="#FFF7E6"
                  px={2}
                  py={1}
                  borderRadius="full"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >
                  <DollarCoinsIcon height="15px" width="15px" />
                  <Text fontSize="sm" color="#D46B08">
                    {creditsAvailable}
                  </Text>
                </Box>
                <Pressable onPress={() => setShowCreditsPopup(true)}>
                  <Text
                    fontSize="sm"
                    color="#00A09B"
                    textDecorationLine="underline"
                  >
                    {Loc.currentLang.manageConversation.buyMoreCredits}
                  </Text>
                </Pressable>
              </HStack>
            </HStack>

            {planType === "MyPayments" ? (
              <VStack space={4} flex={1} overflowY="auto">
                {filteredSortedPayments.map((payment, index) => (
                  <Box
                    key={payment.id ?? index}
                    borderBottomWidth={
                      index !== filteredSortedPayments.length - 1 ? "1px" : "0"
                    }
                    borderBottomColor="gray.200"
                    py={3}
                  >
                    <HStack justifyContent="space-between" alignItems="center">
                      <HStack space={3} alignItems="center">
                        <Pressable
                          onPress={() => {
                            const target = payment.entity;
                            if (!target) return;
                            navigate(`/profile/${target.id}/${target.region}`, {
                              state: {
                                user: {
                                  id: target.id,
                                  pk: target.pk,
                                  region: target.region,
                                  type: target.type,
                                  name: target.title,
                                },
                              },
                            });
                          }}
                        >
                          <ProfilePic
                            width={49}
                            height={49}
                            link={payment.entity}
                          />
                        </Pressable>
                        <VStack>
                          <Text fontWeight="medium" fontSize="md">
                            {payment?.paymentReason == "C"
                              ? Loc.currentLang.purchaseCredits.Credits
                              : Loc.currentLang.donate.donation}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {payment.date ? formatDate(payment.date) : ""}
                          </Text>
                        </VStack>
                      </HStack>
                      <Text fontWeight="bold" fontSize="md">
                        ${payment.amount.toFixed(2)}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : planType === "MyTransactions" ? (
              <VStack space={4} flex={1} overflowY="auto">
                {myPlans.map((txn, index) => (
                  <Box
                    key={txn.id ?? index}
                    borderBottomWidth={
                      index !== myPlans.length - 1 ? "1px" : "0"
                    }
                    borderBottomColor="gray.200"
                    py={3}
                  >
                    <HStack justifyContent="space-between" alignItems="center">
                      <HStack space={3} alignItems="center">
                        <Pressable
                          onPress={() => {
                            const target = txn.target;
                            if (!target) return;
                            navigate(`/profile/${target.id}/${target.region}`, {
                              state: {
                                user: {
                                  id: target.id,
                                  pk: target.pk,
                                  region: target.region,
                                  type: target.type,
                                },
                              },
                            });
                          }}
                        >
                          <ProfilePic
                            width={49}
                            height={49}
                            link={txn.target}
                          />
                        </Pressable>
                        <VStack>
                          <Text fontWeight="medium" fontSize="md">
                            {txn.tranType === "T"
                              ? Loc.currentLang.purchaseCredits.Credits
                              : Loc.currentLang.purchaseCredits.Credits}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {txn.date ? formatDate(txn.date) : ""}
                          </Text>
                        </VStack>
                      </HStack>
                      <Box
                        px={2}
                        py={1}
                        borderRadius="full"
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                      >
                        {/*<DollarCoinsIcon height="15px" width="15px" />*/}
                        <Text fontWeight="bold" fontSize="md">
                          {`${txn?.tranType === "T" ? Loc.currentLang.menu.translation + " " : ""}${Math.abs(txn.credits)}`}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : null}

            <Box pt={4} borderTopWidth="1px" borderTopColor="gray.200" mt={2}>
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontWeight="semibold">
                  {planType === "MyPayments"
                    ? Loc.currentLang.purchaseCredits.totalPayments
                    : planType === "MyCollections"
                      ? Loc.currentLang.purchaseCredits.totalCollections
                      : Loc.currentLang.purchaseCredits.totalTxt}
                </Text>

                {planType === "MyPayments" &&
                Array.isArray(filteredSortedPayments) ? (
                  <Text fontWeight="bold" fontSize="lg">
                    $
                    {(filteredSortedPayments as AV.ICollectLog[])
                      .reduce((sum, payment) => sum + (payment?.amount || 0), 0)
                      .toFixed(2)}
                  </Text>
                ) : planType === "MyTransactions" &&
                  Array.isArray(filteredSortedPayments) ? (
                  <Box
                    px={2}
                    py={1}
                    borderRadius="full"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                  >
                    {/*<DollarCoinsIcon height="15px" width="15px" />*/}
                    <Text fontWeight="bold" fontSize="lg" ml={1}>
                      {(filteredSortedPayments as AV.ICreditTransaction[])
                        .reduce(
                          (sum, txn) => sum + Math.abs(txn?.credits || 0),
                          0,
                        )
                        .toString()}
                    </Text>
                  </Box>
                ) : null}
              </HStack>
            </Box>
          </>
        )}
      </Box>

      {showCreditsPopup && (
        <PurchaseCreditsPopup
          entity={Entity}
          setSharedPlan={setSharedPlan}
          showCreditsPopup={showCreditsPopup}
          setShowCreditsPopup={setShowCreditsPopup}
          triggerBuyCredits={triggerBuyCredits}
          setTriggerBuyCredits={setTriggerBuyCredits}
        />
      )}
    </>
  );
}

export default MyFunds;
